import type {
  Brand, Model, Banner, SiteConfig, DashboardStats,
  AdminColorItem, AdminModelWithColors,
  CreateBrandRequest, UpdateBrandRequest,
  CreateModelRequest, UpdateModelRequest,
  CreateColorRequest, UpdateColorStatusRequest,
  CreateBannerRequest, UpdateBannerRequest,
  ReorderRequest, UpdateSiteConfigRequest, UploadResponse,
  LoginResponse,
} from '@bosamie/shared';

const API_BASE = '/api';
const TOKEN_KEY = 'bosamie_admin_token';

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function setToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token);
}

export function clearToken(): void {
  localStorage.removeItem(TOKEN_KEY);
}

async function adminFetch<T>(path: string, options: RequestInit = {}): Promise<T> {
  const token = getToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...options.headers as Record<string, string>,
  };
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const res = await fetch(`${API_BASE}${path}`, { ...options, headers });

  if (res.status === 401) {
    clearToken();
    window.location.href = '/login';
    throw new Error('Unauthorized');
  }

  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Network error' }));
    throw new Error(err.error || `HTTP ${res.status}`);
  }

  return res.json();
}

async function publicFetch<T>(path: string): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

// ===== Auth =====
export const authApi = {
  login: (pin: string) =>
    adminFetch<LoginResponse>('/admin/login', {
      method: 'POST',
      body: JSON.stringify({ pin }),
    }),
};

// ===== Config =====
export const configApi = {
  get: () => adminFetch<SiteConfig>('/admin/config'),
  update: (data: UpdateSiteConfigRequest) =>
    adminFetch<SiteConfig>('/admin/config', {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
};

// ===== Stats =====
export const statsApi = {
  get: () => adminFetch<DashboardStats>('/admin/stats'),
};

// ===== Brands =====
export const brandApi = {
  list: () => adminFetch<(Brand & { modelCount: number })[]>('/admin/brands'),
  create: (data: CreateBrandRequest) =>
    adminFetch<Brand>('/admin/brands', { method: 'POST', body: JSON.stringify(data) }),
  update: (id: string, data: UpdateBrandRequest) =>
    adminFetch<Brand>(`/admin/brands/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id: string) =>
    adminFetch<{ success: boolean }>(`/admin/brands/${id}`, { method: 'DELETE' }),
  reorder: (ids: string[]) =>
    adminFetch<{ success: boolean }>('/admin/brands/reorder', {
      method: 'PUT', body: JSON.stringify({ ids } as ReorderRequest),
    }),
};

// ===== Models =====
export const modelApi = {
  list: (brandId: string) =>
    adminFetch<(Model & { saleColorCount: number })[]>(`/admin/brands/${brandId}/models`),
  create: (brandId: string, data: CreateModelRequest) =>
    adminFetch<Model>(`/admin/brands/${brandId}/models`, {
      method: 'POST', body: JSON.stringify(data),
    }),
  update: (brandId: string, id: string, data: UpdateModelRequest) =>
    adminFetch<Model>(`/admin/brands/${brandId}/models/${id}`, {
      method: 'PUT', body: JSON.stringify(data),
    }),
  delete: (brandId: string, id: string) =>
    adminFetch<{ success: boolean }>(`/admin/brands/${brandId}/models/${id}`, {
      method: 'DELETE',
    }),
  updatePrice: (id: string, price: number) =>
    adminFetch<{ id: string; price: number }>(`/admin/models/${id}/price`, {
      method: 'PUT', body: JSON.stringify({ price }),
    }),
  updateBadge: (id: string, badge: string[]) =>
    adminFetch<{ id: string; badge: string[] }>(`/admin/models/${id}/badge`, {
      method: 'PUT', body: JSON.stringify({ badge }),
    }),
  reorder: (brandId: string, ids: string[]) =>
    adminFetch<{ success: boolean }>(`/admin/brands/${brandId}/models/reorder`, {
      method: 'PUT', body: JSON.stringify({ ids } as ReorderRequest),
    }),
};

// ===== Colors =====
export const colorApi = {
  getWithColors: (brandId: string, modelId: string) =>
    adminFetch<AdminModelWithColors>(`/admin/brands/${brandId}/models/${modelId}/colors`),
  create: (brandId: string, modelId: string, data: CreateColorRequest) =>
    adminFetch<AdminColorItem>(`/admin/brands/${brandId}/models/${modelId}/colors`, {
      method: 'POST', body: JSON.stringify(data),
    }),
  updateStatus: (id: string, status: string) =>
    adminFetch<{ id: string; status: string }>(`/admin/colors/${id}/status`, {
      method: 'PUT', body: JSON.stringify({ status } as UpdateColorStatusRequest),
    }),
  updateCover: (id: string, coverUrl: string) =>
    adminFetch<{ id: string; coverUrl: string }>(`/admin/colors/${id}/cover`, {
      method: 'PUT', body: JSON.stringify({ coverUrl }),
    }),
  updateDetails: (id: string, detailUrls: string[]) =>
    adminFetch<{ id: string; detailUrls: string[] }>(`/admin/colors/${id}/details`, {
      method: 'PUT', body: JSON.stringify({ detailUrls }),
    }),
  delete: (id: string) =>
    adminFetch<{ success: boolean }>(`/admin/colors/${id}`, { method: 'DELETE' }),
};

// ===== Banners =====
export const bannerApi = {
  list: () => adminFetch<Banner[]>('/admin/banners'),
  create: (data: CreateBannerRequest) =>
    adminFetch<Banner>('/admin/banners', { method: 'POST', body: JSON.stringify(data) }),
  update: (id: string, data: UpdateBannerRequest) =>
    adminFetch<Banner>(`/admin/banners/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id: string) =>
    adminFetch<{ success: boolean }>(`/admin/banners/${id}`, { method: 'DELETE' }),
  reorder: (ids: string[]) =>
    adminFetch<{ success: boolean }>('/admin/banners/reorder', {
      method: 'PUT', body: JSON.stringify({ ids } as ReorderRequest),
    }),
};

// ===== Upload =====
export const uploadApi = {
  upload: async (file: File): Promise<string> => {
    const formData = new FormData();
    formData.append('file', file);
    const token = getToken();
    const res = await fetch(`${API_BASE}/admin/upload`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: formData,
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: 'Upload failed' }));
      throw new Error(err.error);
    }
    const data = await res.json() as UploadResponse;
    return data.url;
  },
};
